import { lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { HOME_THEME, MONO, SANS } from "@/components/home-a/theme";
import { useLanguage } from "@/contexts/LanguageContext";
import { DEMO_META, type DemoKind } from "./demoKinds";
import { StartFreeLink, useStartFree } from "@/components/site/StartFree";

const DemoReportBody = lazy(() => import("./DemoReportBody"));

/**
 * A sample report in a large dialog: the real report component on a demo
 * page, tabs working, with the plans and account creation one click away.
 * Marketing pages open it from "See a sample report"; the report code and its
 * data load only when it opens.
 */
export function DemoReportDialog({ kind, open, onOpenChange }: { kind: DemoKind; open: boolean; onOpenChange: (v: boolean) => void }) {
  const { language } = useLanguage();
  const isEl = language === "el";
  const tr = (el: string, en: string) => (isEl ? el : en);
  const meta = DEMO_META[kind];
  const start = useStartFree();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        style={{ ...HOME_THEME, fontFamily: SANS }}
        className="flex h-[94vh] max-h-[94vh] w-[97vw] max-w-[1320px] flex-col gap-0 overflow-hidden rounded-[22px] border-[hsl(var(--ec-line-strong))] bg-background p-0 text-foreground"
      >
        <div className="flex items-center gap-3 border-b border-border px-5 py-4 pr-12">
          <div className="min-w-0">
            <DialogTitle className="text-[16px] font-semibold">{tr(meta.title[0], meta.title[1])}</DialogTitle>
            <DialogDescription className="truncate text-[12.5px] text-[hsl(var(--ec-muted))]">{tr(meta.sub[0], meta.sub[1])}</DialogDescription>
          </div>
          <span className="ml-auto hidden rounded-full border border-[hsl(var(--warning)/0.35)] bg-[hsl(var(--warning)/0.1)] px-2.5 py-1 text-[11px] text-[hsl(var(--warning))] sm:inline" style={{ fontFamily: MONO }}>
            demo
          </span>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-2 sm:p-4">
          {open && (
            <Suspense fallback={<div className="h-[60vh] animate-pulse rounded-[18px] bg-foreground/[0.03]" />}>
              <DemoReportBody kind={kind} lang={isEl ? "el" : "en"} />
            </Suspense>
          )}
        </div>
        <div data-section="demo_dialog" className="flex flex-col items-stretch gap-3 border-t border-border bg-card/80 px-5 py-4 sm:flex-row sm:items-center">
          <p className="min-w-0 flex-1 text-[14px] leading-snug text-[hsl(var(--ec-text-2))]">
            <span className="text-foreground">{tr("Αυτό παίρνετε για κάθε σελίδα σας.", "You get this report for every page you analyse.")}</span>{" "}
            {start.pitch}
          </p>
          <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
            <Link to="/pricing" className="ec-cta-white inline-flex h-11 shrink-0 items-center whitespace-nowrap justify-center rounded-[10px] px-5 text-[14.5px] font-semibold">
              {tr("Δείτε τα πλάνα", "See the plans")}
            </Link>
            <StartFreeLink location="demo_dialog" className="h-11 text-[14.5px]" />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
